import { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro'
import { gridFormatters, gridValueGetters } from 'src/utils/grid'
import { Payment } from 'src/interfaces/payment'
import { PaymentStatusChip } from 'src/components/payment/grid/PaymentStatusChip'
import { PaymentsItemActionButton } from 'src/components/payment/grid/action/PaymentsItemActionButton'
import { Typography } from '@mui/material'
import { PaymentFailureReasonTooltip } from 'src/components/payment/PaymentFailureReasonTooltip'
import tw from 'twin.macro'
import { PaymentStatus } from './enums'
import { TitledTooltip } from 'src/components/shared/TitledTooltip'
import { colors } from 'src/theme'
import { RiskMinor } from '@shopify/polaris-icons'
import { PayoutMethodTitles } from './constants'

const paymentStatusesToShowDetails = [PaymentStatus.Failed, PaymentStatus.AutoRetry]

export const paymentColumns: Array<GridColDef> = [
  {
    field: 'partnerDriverExternalId',
    headerName: 'Contractors ID',
    sortable: true,
    flex: 2,
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 2,
    sortable: true,
    renderCell: (params: GridRenderCellParams<Payment>) =>
      params.row.paidInGracePeriod ? (
        <TitledTooltip
          title={params.value}
          content={
            <Typography variant="body2" color={colors.SHADES_GREY_300}>
              This contractor is deactivated but payment was processed because they are still within
              the 14 days grace period. Without reactivation, future payments payments to this
              contract may fail. To continue to pay this contractor, they must update their
              documentation to meet compliance requirements.
            </Typography>
          }
        >
          <div css={tw`flex flex-row items-center`}>
            <RiskMinor width={20} height={20} css={tw`mr-1`} fill={colors.COLORS_RED_500} />
            <Typography
              fontWeight={500}
              variant="subtitle1"
              color={colors.COLORS_RED_500}
              css={tw`ml-2 underline`}
            >
              {params.value}
            </Typography>
          </div>
        </TitledTooltip>
      ) : (
        params.value
      ),
  },
  {
    field: 'marketName',
    headerName: 'Market',
    flex: 2,
    sortable: false,
  },
  {
    field: 'uploadedAt',
    headerName: 'Added on',
    flex: 2,
    sortable: true,
    type: 'dateTime',
    valueGetter: gridValueGetters.parseDate,
    valueFormatter: gridFormatters.formatShortDate,
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 2,
    sortable: true,
    renderCell: (params: GridRenderCellParams<Payment>) => (
      <div css={tw`flex items-center`}>
        <PaymentStatusChip status={params.value} />
        {paymentStatusesToShowDetails.includes(params.row.status) &&
          !!params.row.failureReasonType && (
            <PaymentFailureReasonTooltip payment={params.row}>
              <Typography
                fontWeight={500}
                variant="subtitle1"
                color="black"
                css={tw`ml-2 underline`}
              >
                Details
              </Typography>
            </PaymentFailureReasonTooltip>
          )}
      </div>
    ),
  },
  {
    field: 'paidAt',
    headerName: 'Paid on date',
    flex: 2,
    sortable: true,
    type: 'dateTime',
    valueGetter: gridValueGetters.parseDate,
    valueFormatter: gridFormatters.formatShortDate,
  },
  {
    field: 'payoutMethod',
    headerName: 'Payout Method',
    flex: 2,
    valueGetter: (params: GridValueGetterParams<Payment>) =>
      PayoutMethodTitles[params.row.payoutMethod] || params.row.payoutMethod || '-',
    sortable: true,
  },
  {
    field: 'paid_to',
    headerName: 'Paid to',
    flex: 2,
    sortable: false,
    valueGetter: (params: GridValueGetterParams<Payment>) =>
      params.row.paidTo?.trim() ||
      `${params.row.destinationType || ''}${
        params.row.destinationLast4 ? ` •• ${params.row.destinationLast4}` : ''
      }` ||
      '-',
  },
  {
    field: 'amount',
    headerName: 'Amount',
    flex: 1.5,
    sortable: true,
    valueFormatter: gridFormatters.formatCurrency,
  },
  {
    field: 'netAmount',
    headerName: 'Net amount',
    flex: 1.5,
    sortable: true,
    valueFormatter: gridFormatters.formatCurrency,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 1,
    sortable: false,
    disableExport: true,
    renderCell: (params: GridRenderCellParams<Payment>) => (
      <PaymentsItemActionButton payment={params.row} />
    ),
  },
]

export const paymentPageSizeOptions = [100, 250, 500, 1000]
